import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { type MouseEvent, useEffect, useRef, useState } from "react";

import {
  buildReminderShillTrackingContext,
  INTERNAL_REMINDER_SHILL_FEATURE_FLAG,
} from "@/components/note/floating-actions/reminder-shill-actions";
import {
  REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE,
  ReminderShillOverflowTooltipText,
} from "@/components/reminder-shill/reminder-shill-overflow-tooltip-text";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { buildReminderShillUpsellAttributionContext } from "@/modules/reminder-shill-upsell-attribution";
import { useAppStore } from "@/store";
import type { INoteObservable } from "@/store/note";
import { isReminderShillFeatureFlagEnabled } from "@/store/note/reminder-shill-feature-flag";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

interface HomeReminderShillActionsProps {
  noteObservable: INoteObservable;
}

interface HomeReminderShillActionButtonProps {
  "aria-label": string;
  iconKind: MDSIconKind;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
}

/**
 * Renders hydrated reminder shill text inside Home rows and owns the same action semantics
 * as the editor reminder shill surface.
 */
export const HomeReminderShillActions = observer(function HomeReminderShillActions({
  noteObservable,
}: HomeReminderShillActionsProps) {
  const { store } = useAppStore();
  const reminderShill = noteObservable.reminderShill;
  const shouldShowReminderShill =
    isReminderShillFeatureFlagEnabled({
      getFlagStatus: flag => store.spaceAccountFeatureFlags.getFlagStatus(flag),
    }) && noteObservable.shouldShowReminderShill;
  const lastVisibleTrackedIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<"dismiss" | "click" | null>(null);
  const hasProactiveAccess = store.subscriptions.hasProactiveAccess;
  const usesInternalReminderShillCopy =
    store.spaceAccountFeatureFlags.getFlagStatus(INTERNAL_REMINDER_SHILL_FEATURE_FLAG) === true;
  const shouldUseProactiveReminderShill = hasProactiveAccess && !usesInternalReminderShillCopy;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!reminderShill || !shouldShowReminderShill) return;
    if (lastVisibleTrackedIdRef.current === reminderShill.id) return;

    lastVisibleTrackedIdRef.current = reminderShill.id;
    trackEvent(
      TrackedEvent.EditorReminderShillPillVisible,
      buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
    );
  }, [noteObservable, reminderShill, shouldShowReminderShill, store]);

  if (!reminderShill || !shouldShowReminderShill) return null;

  const isTriggeredProactiveReminderShill =
    shouldUseProactiveReminderShill && reminderShill.is_clicked;
  if (isTriggeredProactiveReminderShill) return null;

  const copy = shouldUseProactiveReminderShill
    ? `Remind me about ${reminderShill.text}.`
    : `Get reminded about ${reminderShill.text}?`;
  const actionLabel = shouldUseProactiveReminderShill
    ? "Create reminder"
    : "Open Mem Agent settings";
  const pendingActionLabel = shouldUseProactiveReminderShill
    ? "Creating reminder"
    : "Opening Mem Agent settings";

  const handleDismiss = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setPendingAction("dismiss");

    try {
      await noteObservable.dismissReminderShill();
      trackEvent(
        TrackedEvent.EditorReminderShillPillDismissed,
        buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
      );
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setPendingAction(null);
      }
    }
  };

  const handleClick = async () => {
    if (isSubmittingRef.current || isTriggeredProactiveReminderShill) return;
    isSubmittingRef.current = true;
    setPendingAction("click");

    try {
      if (shouldUseProactiveReminderShill) {
        await noteObservable.clickReminderShill();
      } else {
        store.settingsModal.open(SettingsTabId.MemAgentPage, {
          source: "editor_reminder_shill",
          reminderShillUpsellAttributionContext: buildReminderShillUpsellAttributionContext({
            noteObservable,
            reminderShill,
            source: "editor_reminder_shill",
          }),
        });
      }
      trackEvent(
        TrackedEvent.EditorReminderShillPillSettingsLaunched,
        buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
      );
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setPendingAction(null);
      }
    }
  };

  return (
    <StyledContainer data-disable-note-preview-on-hover="true">
      <StyledTextTooltip label={copy} text={copy}>
        <StyledText
          as="span"
          color={MDSTextColor.Emphasis}
          overflow={MDSTextOverflow.Ellipsis}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.Regular}
          {...{ [REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE]: "true" }}
        >
          {copy}
        </StyledText>
      </StyledTextTooltip>
      <StyledActions>
        <MDSTooltip
          config={{
            label: "Yes",
            placement: MDSTooltipPlacement.Top,
          }}
        >
          <HomeReminderShillActionButton
            aria-label={pendingAction === "click" ? pendingActionLabel : actionLabel}
            iconKind={MDSIconKind.Check}
            isDisabled={pendingAction !== null || isTriggeredProactiveReminderShill}
            isLoading={pendingAction !== null}
            onClick={handleClick}
          />
        </MDSTooltip>
        <MDSTooltip
          config={{
            label: "No",
            placement: MDSTooltipPlacement.Top,
          }}
        >
          <HomeReminderShillActionButton
            aria-label={
              pendingAction === "dismiss"
                ? "Dismissing reminder suggestion"
                : "Dismiss reminder suggestion"
            }
            iconKind={MDSIconKind.Exit}
            isDisabled={pendingAction !== null}
            isLoading={pendingAction !== null}
            onClick={handleDismiss}
          />
        </MDSTooltip>
      </StyledActions>
    </StyledContainer>
  );
});

function HomeReminderShillActionButton({
  "aria-label": ariaLabel,
  iconKind,
  isDisabled,
  isLoading,
  onClick,
}: HomeReminderShillActionButtonProps) {
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    onClick();
  };

  const stopRowInteraction = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <StyledActionButton
      aria-label={ariaLabel}
      iconKind={iconKind}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onClick={handleClick}
      onMouseDown={stopRowInteraction}
      rounded
      size={MDSIconButtonSize.XSmall}
      type="button"
      variant={MDSIconButtonVariant.OnEmphasis}
    />
  );
}

const StyledContainer = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing["2"]};
  color: ${({ theme }) => theme.colors.text.emphasis};
`;

const StyledText = styled(MDSText)`
  flex: 0 1 auto;
  min-width: 0;
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
`;

const StyledTextTooltip = styled(ReminderShillOverflowTooltipText)`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
`;

const StyledActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const StyledActionButton = styled(MDSIconButton)`
  flex: 0 0 16px;
  width: 16px;
  min-width: 16px;
  height: 16px;
  min-height: 16px;
`;
